
<template>
  <v-form ref="formRef" class="rlt-form method-form">
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-link-variant</v-icon>
        <span>Erzeuger-Verknüpfung</span>
      </div>

      <!-- Generator Type and Name init -->
      <v-checkbox
        v-model="form.generatorUsage"
        :error-messages="validationErrors?.generatorUsage"
        label="verknüpfter Erzeuger (optional)"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.generator_usage" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.generator_usage }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.generator_usage"
            icon
            @click="copyFromReference('generator_usage')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>
      <!-- Cold generator: type -->
      <v-autocomplete
        v-if="form.coolingRegister && form.generatorUsage"
        v-model="form.coldgenType"
        :items="coldgenTypes"
        :error-messages="validationErrors?.coldgen_type"
        label="Kältequelle"
        :loading="!actionList?.length"
        clearable
        item-title="name" 
        item-value="name"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.coldgen_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.coldgen_type }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.coldgen_type"
            icon
            @click="copyFromReference('coldgen_type')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Cold generator: name -->
      <v-autocomplete
        v-if="form.coldgenType"
        v-model="form.coldgenName"
        :items="filteredColdgenNames"
        item-title="name"
        item-value="id"
        :error-messages="validationErrors?.coldgen_name"
        label="Kälteerzeuger"
        validate-on-blur
        class="mb-3 ml-8"
        @update:model-value="filterColdProducer"
      >
        <template #append>
          <v-tooltip v-if="hint.coldgen_name" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.coldgen_name }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Heat generator: type -->
      <v-autocomplete
        v-if="form.generatorUsage"
        v-model="form.generatorType"
        :items="generatorTypes"
        :loading="!actionList?.length"
        :error-messages="validationErrors?.generator_type"
        label="Wärmequelle"
        clearable
        validate-on-blur
        class="mb-3"
        @update:model-value="onGeneratorTypeChange"
      >
        <template #append>
          <v-tooltip v-if="hint.generator_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.generator_type }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.generator_type"
            icon
            @click="copyFromReference('generator_type')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
      </div>

      <!-- Heat generator: name -->
      <v-autocomplete
        v-if="form.generatorType"
        v-model="form.generatorName"
        :items="filteredGeneratorNames"
        item-title="name"
        item-value="id"
        :error-messages="validationErrors?.action_id"
        label="Wärmeerzeuger"
        validate-on-blur
        class="mb-3 ml-8"
        @update:model-value="filterProducer"
      >
        <template #append>
          <v-tooltip v-if="hint.action_id" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.action_id }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Anzahl -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">mdi-counter</v-icon>
          <span>Basisdaten</span>
        </div>

        <v-text-field
          v-model.number="form.amount"
          :error-messages="validationErrors?.amount"
          label="Anzahl"
          :hint="hint.amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-btn
              v-if="!reference && referenceData?.amount"
              icon
              @click="copyFromReference('amount')"
              title="Referenzwert übernehmen"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

    <!-- Zuluft --> 
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-air-filter</v-icon>
        <span>Zuluft</span>
      </div>

      <v-text-field
        v-model.number="form.volumeFlow"
        type="number"
        :error-messages="validationErrors?.volume_flow"
        label="Volumenstrom"
        suffix="m³/h"
        :hint="hint.volume_flow"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.volume_flow"
            icon
            @click="copyFromReference('volume_flow')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="form.volumeFlowDrawDown"
        type="number"
        :error-messages="validationErrors?.volume_flow_draw_down"
        label="Absenkvolumenstrom"
        suffix="m³/h"
        v-show="form.drawDownOperation"
        :hint="hint.volume_flow_draw_down"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.volume_flow_draw_down"
            icon
            @click="copyFromReference('volume_flow_draw_down')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="form.pressureSup"
        type="number"
        :error-messages="validationErrors?.pressure_sup"
        label="Druckverlust"
        suffix="Pa"
        v-show="!sharedData?.external_fan_calculation"
        :hint="hint.pressure_sup"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.pressure_sup"
            icon
            @click="copyFromReference('pressure_sup')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="etaSupPercentage"
        type="number"
        :error-messages="validationErrors?.eta_sup"
        label="Systemwirkungsgrad"
        suffix="%"
        v-show="!sharedData?.external_fan_calculation"
        :hint="hint.eta_sup"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.eta_sup"
            icon
            @click="copyFromReference('eta_sup')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Abluft -->
    <div class="method-section">
      <div class="method-section-heading mt-5">
        <v-icon size="22" color="primary">mdi-fan</v-icon>
        <span>Abluft</span>
      </div>

      <v-text-field
        v-model.number="form.volumeFlowEta"
        type="number"
        :error-messages="validationErrors?.volume_flow_eta"
        label="Volumenstrom"
        suffix="m³/h"
        :hint="hint.volume_flow_eta"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.volume_flow_eta"
            icon
            @click="copyFromReference('volume_flow_eta')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="form.volumeFlowDrawDownEta"
        type="number"
        :error-messages="validationErrors?.volume_flow_draw_down_eta"
        label="Absenkvolumenstrom"
        suffix="m³/h"
        v-show="form.drawDownOperation"
        :hint="hint.volume_flow_draw_down_eta"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.volume_flow_draw_down_eta"
            icon
            @click="copyFromReference('volume_flow_draw_down_eta')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="form.pressureEta"
        type="number"
        :error-messages="validationErrors?.pressure_eta"
        label="Druckverlust"
        suffix="Pa"
        v-show="!sharedData?.external_fan_calculation"
        :hint="hint.pressure_eta"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.pressure_eta"
            icon
            @click="copyFromReference('pressure_eta')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model.number="etaEtaPercentage"
        type="number"
        :error-messages="validationErrors?.eta_eta"
        label="Systemwirkungsgrad"
        suffix="%"
        v-show="!sharedData?.external_fan_calculation"
        :hint="hint.eta_eta"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.eta_eta"
            icon
            @click="copyFromReference('eta_eta')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Betrieb -->
    <div class="method-section">
      <div class="method-section-heading mt-5">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betrieb</span>
      </div>

      <v-autocomplete
        v-model="form.uptime"
        :items="uptimes"
        :error-messages="validationErrors?.uptime"
        label="Tägliche Betriebsstunden (ohne Absenkbetrieb)"
        suffix="h"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            @click="copyFromReference('uptime')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-text-field
        v-model.number="form.daysPerYear"
        type="number"
        :disabled="!form.uptime"
        :error-messages="validationErrors?.days_per_year"
        label="Betriebstage pro Jahr"
        suffix="d"
        :hint="hint.days_per_year"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.days_per_year" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.days_per_year }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.days_per_year"
            icon
            @click="copyFromReference('days_per_year')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-checkbox
        v-model="form.drawDownOperation"
        :error-messages="validationErrors?.draw_down_operation"
        label="Absenkbetrieb"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.draw_down_operation" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.draw_down_operation }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.draw_down_operation"
            icon
            @click="copyFromReference('draw_down_operation')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <v-text-field
        v-model.number="form.dailyDrawDown"
        type="number"
        :disabled="!form.drawDownOperation"
        v-show="form.drawDownOperation"
        :error-messages="validationErrors?.daily_draw_down"
        label="Tägliche Betriebsstunden im Absenkbetrieb"
        suffix="h"
        :hint="hint.daily_draw_down"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.daily_draw_down"
            icon
            @click="copyFromReference('daily_draw_down')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Luftzustände / Befeuchtung -->
    <div class="method-section">
      <div class="method-section-heading mt-5">
        <v-icon size="22" color="primary">mdi-thermometer-water</v-icon>
        <span>Luftzustände und Befeuchtung</span>
      </div>

      <v-autocomplete
        v-model="form.supplyAirTemperature"
        :items="supplyAirTemperatures"
        :error-messages="validationErrors?.supply_air_temperature"
        label="Zulufttemperatur"
        suffix="°C"
        :hint="hint.supply_air_temperature"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.supply_air_temperature" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.supply_air_temperature }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.supply_air_temperature"
            icon
            @click="copyFromReference('supply_air_temperature')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.humidityRequest"
        :items="humidityRequests"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.humidity_request"
        label="Feuchteanforderung"
        :hint="hint.humidity_request"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.humidity_request" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.humidity_request }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.humidity_request"
            icon
            @click="copyFromReference('humidity_request')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="form.humidifier"
        :items="humidifiers"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.humidifier"
        label="Befeuchter"
        :hint="hint.humidifier"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.humidifier" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.humidifier }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.humidifier"
            icon
            @click="copyFromReference('humidifier')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Heating -->
    <div class="method-section">
      <div class="method-section-heading mt-5">
        <v-icon size="22" color="primary">mdi-fire</v-icon>
        <span>Heizen und Kühlen</span>
      </div>

      <v-autocomplete
        v-if="!form.generatorType && !form.generatorName"
        v-model="form.heatType"
        :items="heatTypes"
        item-title="name"
        item-value="id"
        :error-messages="validationErrors?.heat_type"
        label="Heizart"
        :hint="hint.heat_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.heat_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.heat_type }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.heat_type"
            icon
            @click="copyFromReference('heat_type')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Readonly fuel name when generator selected -->
      <v-text-field
        v-if="form.generatorName"
        v-model="fuelName"
        type="text"
        :error-messages="validationErrors?.fuel_name"
        label="Heizart"
        :readonly="true"
        :hint="hint.fuel_name"
        validate-on-blur
        class="mb-3"
      />

      <!-- cop value when strom -->
      <v-text-field
        v-if="form.heatType === 1"
        v-model.number="form.cop"
        type="number"
        :error-messages="validationErrors?.cop"
        label="COP"
        :hint="hint.cop"
        validate-on-blur
        class="mb-3"
      />

      <!-- Heat recovery -->
      <v-autocomplete
        v-model="form.heatRecovery"
        :items="heatRecoveries"
        item-title="text"
        item-value="value"
        :error-messages="validationErrors?.heat_recovery"
        label="Wärmerückgewinnungs- bzw. Umluftsystem"
        :hint="hint.heat_recovery"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.heat_recovery" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.heat_recovery }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.heat_recovery"
            icon
            @click="copyFromReference('heat_recovery')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <v-text-field
        v-model.number="form.heatRecoveryCoefficient"
        type="number"
        :error-messages="validationErrors?.heat_recovery_coefficient"
        label="Wirkungsgrad Wärmerückgewinnung"
        suffix="%"
        :hint="hint.heat_recovery_coefficient"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.heat_recovery_coefficient"
            icon
            @click="copyFromReference('heat_recovery_coefficient')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <v-checkbox
        v-model="form.coolingRegister"
        :error-messages="validationErrors?.cooling_register"
        label="Kühlregister"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cooling_register" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.cooling_register }}</span>
          </v-tooltip>
          <v-btn
            v-if="!reference && referenceData?.cooling_register"
            icon
            @click="copyFromReference('cooling_register')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- EER (free input if no coldgen selected) -->
      <v-text-field
        v-if="form.coolingRegister && !form.coldgenType && !form.coldgenName"
        v-model.number="form.eer"
        type="number"
        :error-messages="validationErrors?.eer"
        label="EER"
        :hint="hint.eer"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-btn
            v-if="!reference && referenceData?.eer"
            icon
            @click="copyFromReference('eer')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- EER (readonly if coldgenName selected) -->
      <v-text-field
        v-if="form.coldgenName"
        v-model.number="form.eer"
        type="number"
        :error-messages="validationErrors?.eer"
        label="EER"
        :readonly="true"
        :hint="hint.eer"
        validate-on-blur
        class="mb-3"
      />
    </div>
  </v-form>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import { useProjectStore } from '@/stores/projectStore'
import { storeToRefs } from 'pinia'
import api from '@/plugins/axios'
import { djangoChoicesToVuetify } from '@/utils'

/* ---------------- PROPS ---------------- */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },  // v-model
  reference: { type: Boolean, default: false },
  planned: { type: Boolean, default: false },
  otherData: { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

/* ---------------- Pinia (actions list for generator filters) ---------------- */
const projectStore = useProjectStore()
const { currentProjectActions: actionList } = storeToRefs(projectStore)

/* ---------------- FIELD MAP (backend ↔ frontend) ---------------- */
const fields = {
  // base (from BaseForm pattern)
  generatorType: 'generator_type',
  generatorName: 'action_id',
  generatorUsage: 'generator_usage',

  // cold generator
  coldgenType: 'coldgen_type',
  coldgenName: 'coldgen_name',

  // basic
  amount: 'amount',

  // supply
  volumeFlow: 'volume_flow',
  volumeFlowDrawDown: 'volume_flow_draw_down',
  pressureSup: 'pressure_sup',
  etaSup: 'eta_sup',

  // exhaust
  volumeFlowEta: 'volume_flow_eta',
  volumeFlowDrawDownEta: 'volume_flow_draw_down_eta',
  pressureEta: 'pressure_eta',
  etaEta: 'eta_eta',

  // operation
  uptime: 'uptime',
  daysPerYear: 'days_per_year',
  drawDownOperation: 'draw_down_operation',
  dailyDrawDown: 'daily_draw_down',

  // air states
  supplyAirTemperature: 'supply_air_temperature',
  humidityRequest: 'humidity_request',
  humidifier: 'humidifier',

  // heat & cooling
  heatRecovery: 'heat_recovery',
  heatRecoveryCoefficient: 'heat_recovery_coefficient',
  coolingRegister: 'cooling_register',
  eer: 'eer',

  // heating mode (if no generator selected)
  heatType: 'heat_type',
  cop: 'cop'
  // NOTE: fuelName is UI-only; not mapped to backend
}

/* ---------------- useBaseForm: dynamic/universal ---------------- */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,

  // From Option A (Pinia integration): generatorTypes & filteredGeneratorNames
  generatorTypes,
  filteredGeneratorNames
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {
    drawDownOperation: false,
    coolingRegister: false,
    generatorUsage: false
  },
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, payload) => emit(e, payload)
})

/* ---------------- Local state ---------------- */
const formRef = ref(null)
const ready = ref(false)
const fuelName = ref(null)

/* ---------------- Lists from API ---------------- */
const coldgenTypes = ref([])            // string method names for cold generators
const heatTypes = ref([])               // [{id, name}]
const uptimes = ref([])                 // [1..24]
const supplyAirTemperatures = ref([])   // [14..22], Gültigkeitsbereich der DIN V 18599-3
const humidityRequests = ref([])        // [{text, value}]
const humidifiers = ref([])             // [{text, value}]
const heatRecoveries = ref([])          // [{text, value}]

/* ---------------- Computed helpers ---------------- */
const filteredColdgenNames = computed(() => {
  if (!form.coldgenType) return []
  if (!actionList.value?.length) return []

  if (form.coldgenType === 'freegen') {
    // keep only freegen where planned.main_generated_energy === 12
    return actionList.value.filter(
      a => a.method === 'freegen' && a?.planned?.main_generated_energy === 12
    )
  }
  return actionList.value.filter(a => a.method === form.coldgenType)
})

// percent display mapping for etaSup/etaEta
const etaSupPercentage = computed({
  get: () => (form.etaSup ? +(Number(form.etaSup) * 100).toFixed(2) : null),
  set: v => {
    if (v == null || v === '') {
      form.etaSup = null
    } else {
      const num = Number(v)
      form.etaSup = isFinite(num) ? (num / 100).toFixed(4) : null
    }
  }
})

const etaEtaPercentage = computed({
  get: () => (form.etaEta ? +(Number(form.etaEta) * 100).toFixed(2) : null),
  set: v => {
    if (v == null || v === '') {
      form.etaEta = null
    } else {
      const num = Number(v)
      form.etaEta = isFinite(num) ? (num / 100).toFixed(4) : null
    }
  }
})

/* ---------------- Watchers (ported behavior) ---------------- */
// legacy: refetch heat types on selection change (kept for parity)
watch(() => form.heatType, () => { fetchHeatTypes() }, { immediate: true })

// when action list changes, refilter
watch(actionList, () => {
  filterProducer()
  filterColdProducer()
  fetchColdgenTypes()
}, { immediate: true })

// if generatorType cleared after ready: clear dependent fields
watch(() => form.generatorType, (newVal, oldVal) => {
  if (!ready.value) return
  if (oldVal !== null && newVal === null) {
    deleteFlow()
  }
})


// 
watch(() => form.generatorUsage, (newVal, oldVal) => {
  if (oldVal !== null && newVal === null) {
    deleteFlow()
  }
})


// coolingRegister toggled off -> clear coldgen chain
watch(() => form.coolingRegister, (newVal, oldVal) => {
  if (oldVal !== null && newVal === false) {
    form.coldgenType = null
  }
})

// coldgenType cleared -> full cold flow reset
watch(() => form.coldgenType, (newVal, oldVal) => {
  if (oldVal !== null && newVal === null) {
    deleteColdFlow()
  }
})

// on generatorName change: fetch its fuel & heatType
watch(() => form.generatorName, () => { filterProducer() })

// on coldgenName change: derive EER into eer
watch(() => form.coldgenName, () => { filterColdProducer() })

/* ---------------- Lifecycle ---------------- */
onMounted(() => {
  // static-ish lists
  fetchUptimes()
  fetchSupplyAirTemperatures()
  fetchHumidityRequests()
  fetchHumidifiers()
  fetchHeatRecoveries()
  fetchHeatTypes()

  ready.value = true

  // derive from actions
  fetchColdgenTypes()
  filterProducer()
  filterColdProducer()

  fetchHints()
})

/* ---------------- Methods (ported 1:1 behavior) ---------------- */
function onGeneratorTypeChange() {
  // replicates @change="deleteGeneratorName" legacy behavior
  if (!form.generatorType) {
    deleteGeneratorName()
  }
}

function deleteGeneratorName() {
  if (!form.generatorType) {
    form.generatorName = null
    form.heatType = null
    fuelName.value = null
  }
}

function deleteColdFlow() {
  form.coldgenType = null
  form.coldgenName = null
  form.eer = null
}

function deleteFlow() {
  form.generatorType = null
  form.generatorName = null
  form.heatType = null
  fuelName.value = null
}

// cold producer selection -> set EER into eer
function filterColdProducer() {
  if (!form.coldgenName) return
  const action = actionList.value?.find(a => a.id === form.coldgenName)
  if (!action) return

  const eerField = action.method === 'chiller' ? 'eer' : 'main_effort_factor'
  const src = props.reference ? action.reference : action.planned
  const eerValue = src?.[eerField]

  if (!eerValue) {
    form.eer = 0
    return
  }
  form.eer = eerValue
}

// heat generator selection -> set fuelName & heatType
function filterProducer() {
  if (!form.generatorName) return
  const action = actionList.value?.find(a => a.id === form.generatorName)
  if (!action) return

  const src = props.reference ? action.reference : action.planned
  const fuelId = src?.fuel
  if (!fuelId) return

  fetchProducerFuel(fuelId)
}

/* ---------------- API: lists ---------------- */
function fetchColdgenTypes() {
  const gens = (actionList.value || [])
    .filter(a => a.method_type === 'generator' && a.method !== 'boiler')
    .map(a => a.method)
  coldgenTypes.value = [...new Set(gens)]
}

function fetchUptimes() {
  uptimes.value = Array.from({ length: 24 }, (_, i) => i + 1)
}

function fetchSupplyAirTemperatures() {
  // 14..22: DIN V 18599-3, Gl. (26) lässt die Umrechnung der Kennwerte nur in
  // diesem Bereich zu. Die Liste ging bis 24 °C — zwei Werte, die der Server
  // seit der Angleichung an die Norm zurückweist.
  supplyAirTemperatures.value = Array.from({ length: 9 }, (_, i) => 14 + i)
}

async function fetchHumidityRequests() {
  try {
    const { data } = await api.get('/methods/hvac/humidity-requests/')
    humidityRequests.value = djangoChoicesToVuetify(data)
  } catch {}
}

async function fetchHumidifiers() {
  try {
    const { data } = await api.get('/methods/hvac/humidifiers/')
    humidifiers.value = djangoChoicesToVuetify(data)
  } catch {}
}

async function fetchHeatTypes() {
  try {
    const { data } = await api.get('/methods/hvac/heat-sources/')
    heatTypes.value = data // expect [{id, name}]
    if (Array.isArray(heatTypes.value) && heatTypes.value.length === 1) {
      form.heatType = heatTypes.value[0].id
    }
  } catch {}
}

async function fetchHeatRecoveries() {
  try {
    const { data } = await api.get('/methods/hvac/heat-recoveries/')
    heatRecoveries.value = djangoChoicesToVuetify(data)
  } catch {}
}

/* ---------------- API: fetch fuel of selected heat generator ---------------- */
async function fetchProducerFuel(fuel_id) {
  try {
    const { data } = await api.get('/methods/hvac/fuel/', { params: { id: fuel_id } })
    fuelName.value = data?.name ?? null
    form.heatType = data?.id ?? null
  } catch {}
}

/* ---------------- Expose validation ---------------- */
function validateForm() {
  return formRef.value?.validate?.()
}
defineExpose({ validateForm })

/* ---------------- For template usage ---------------- */
const validationErrors = computed(() => props.validationErrors)
</script>


<style scoped lang="scss">
.rlt-form {
  width: 100%;
}

/* Make method forms visually match the modern Action.vue shell */
.rlt-form :deep(.v-field) {
  border-radius: 18px;
}

.rlt-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section headings like Zuluft, Abluft, Betrieb */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Make normal dividers softer */
.rlt-form :deep(.v-divider) {
  border-color: rgba(var(--v-theme-on-surface), 0.08);
}

/* Checkboxes should not feel visually detached */
.rlt-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Better spacing for dependent/nested fields using your ml-8 classes */
.rlt-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.rlt-form :deep(.v-input__append),
.rlt-form :deep(.v-field__append-inner) {
  align-items: center;
}

.rlt-form :deep(.v-input__append .v-icon),
.rlt-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.rlt-form :deep(.v-input__append .v-btn),
.rlt-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.rlt-form :deep(.v-input__append .v-btn:hover),
.rlt-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Better disabled field appearance */
.rlt-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Mobile cleanup */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .rlt-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>